import { coachingApi } from "./coaching-api";
import { type CoachingRequester, requestContract } from "./coaching-services";
import type {
  CoachingAssignmentLogDto,
  CoachingAttendanceLogDto,
  CoachingInviteDispatchDto,
  CoachingInviteLogDto,
  CoachingSessionRecordingsDto,
  CoachingUseCaseCreateRequest,
  CoachingUseCaseListDto,
  CoachingUseCaseResponseDto,
  CoachingWeekWiseProgressDto,
  PostSessionEmailEnqueueDto,
  PostSessionEmailLogDto,
  PostSessionEmailRequest
} from "./coaching-z6-dtos";

type Query = Record<string, string | number | boolean | null | undefined>;

/**
 * Z6 coaching Zoom-integration service family (F1/F2/F4/F5/F8/F9/F10/F13/F14).
 *
 * Layered on the shared coaching transport (requestContract) so org-id injection
 * and the UUID Idempotency-Key discipline are identical to the existing service
 * facade. Reads return the backend's wire shapes verbatim; the typed gated
 * states (dry_run / queued / closed) are NOT normalized away — surfaces render
 * them honestly. The live and mock variants are built from the same factory.
 */
export function createCoachingZ6Services(requester: CoachingRequester = coachingApi) {
  return {
    recordings: {
      // F1: secure recording link + passcode presence (raw passcode never returned).
      forSession: (sessionId: string) =>
        requestContract<CoachingSessionRecordingsDto>(requester, "sessions.recordings", {
          pathParams: { session_id: sessionId }
        })
    },
    inviteLog: {
      // F5: append-only per-attempt invite trigger log for a session.
      forSession: (sessionId: string) =>
        requestContract<CoachingInviteLogDto>(requester, "sessions.inviteLog", {
          pathParams: { session_id: sessionId }
        })
    },
    invites: {
      // F4: resend Zoom invites to all (no participantIds) or selected participants.
      resend: (sessionId: string, participantIds: number[] | undefined, idempotencyKey: string) =>
        requestContract<CoachingInviteDispatchDto>(requester, "sessions.forceResend", {
          pathParams: { session_id: sessionId },
          body: participantIds && participantIds.length > 0 ? { participantIds, force: true } : { force: true },
          idempotencyKey
        })
    },
    postSessionEmail: {
      // F9: enqueue the standardized post-session email (assignment free-text +
      // dynamic merge-field preview). Delivery is fail-closed until the gate opens.
      enqueue: (sessionId: string, body: PostSessionEmailRequest, idempotencyKey: string) =>
        requestContract<PostSessionEmailEnqueueDto>(requester, "sessions.postSessionEmail", {
          pathParams: { session_id: sessionId },
          body,
          idempotencyKey
        }),
      log: (sessionId: string) =>
        requestContract<PostSessionEmailLogDto>(requester, "sessions.postSessionEmailLog", {
          pathParams: { session_id: sessionId }
        })
    },
    progress: {
      // F2: week-wise participant progress grid.
      weekWise: (cohortId: string) =>
        requestContract<CoachingWeekWiseProgressDto>(requester, "cohorts.progressWeekWise", {
          pathParams: { cohort_id: cohortId }
        })
    },
    attendance: {
      // F8: weekly attendance log report.
      forCohort: (cohortId: string) =>
        requestContract<CoachingAttendanceLogDto>(requester, "cohorts.attendance", {
          pathParams: { cohort_id: cohortId }
        })
    },
    assignments: {
      // F10: weekly assignment submission log (Jotform-sourced).
      forCohort: (cohortId: string) =>
        requestContract<CoachingAssignmentLogDto>(requester, "cohorts.assignments", {
          pathParams: { cohort_id: cohortId }
        })
    },
    cohortReassign: {
      // F13: reassign an existing cohort to a new coach (admin lane).
      reassignCoach: (cohortId: string, newCoachId: number, notes: string | undefined, idempotencyKey: string) =>
        requestContract<unknown>(requester, "cohorts.reassignCoach", {
          pathParams: { cohort_id: cohortId },
          body: notes ? { newCoachId, notes } : { newCoachId },
          idempotencyKey
        })
    },
    participants: {
      // F6: remove a participant from a cohort (admin lane); provider fan-out
      // cancels future registrants + calendar events when the gates are open.
      removeFromCohort: (cohortId: string, participantId: string, idempotencyKey: string) =>
        requestContract<unknown>(requester, "participants.removeFromCohort", {
          pathParams: { cohort_id: cohortId, participant_id: participantId },
          idempotencyKey
        })
    },
    useCases: {
      // F14: use-case repository list/create/read.
      list: (query?: Query) =>
        requestContract<CoachingUseCaseListDto>(requester, "useCases.list", { query }),
      detail: (useCaseId: string) =>
        requestContract<CoachingUseCaseResponseDto>(requester, "useCases.detail", {
          pathParams: { use_case_id: useCaseId }
        }),
      create: (body: CoachingUseCaseCreateRequest, idempotencyKey: string) =>
        requestContract<CoachingUseCaseResponseDto>(requester, "useCases.create", { body, idempotencyKey })
    }
  };
}

export type CoachingZ6Services = ReturnType<typeof createCoachingZ6Services>;
export const coachingZ6Services = createCoachingZ6Services();
